import React from 'react';
import Product from '../components/Product';
import OrderBumps from '../components/CheckoutForm/components/OrderBumps';
import Variants from '../components/CheckoutForm/components/Variants';
import CheckoutForm from '../components/CheckoutForm';
import useCheckoutState from '../hooks/useCheckoutState';
import useAiTarget from '../hooks/useAiTarget';
import clsx from 'clsx';
import CheckoutConfirmation from '../CheckoutConfirmation';
import CheckoutFormContainer from '../components/CheckoutFormContainer';
import CheckoutBrandingBadge from '../components/CheckoutBrandingBadge';
import { LogoBanner } from '../components/Product/Product';
import { PAGE_TYPES } from '../enums/page-types';
import EventDetails from '../components/EventDetails';
import { checkHasEventModules, checkHasProductModules } from '../enums/modules';
import useCartCost from '../hooks/useCartCost';
import useEffectivePricing from '../hooks/useEffectivePricing';
import TicketGroups from '../components/TicketGroups';
import ProductPricePicker from '../components/Product/ProductPricePicker';
import { isMultiPrice } from '../lib/multiPrice';
import StyledElement from '../components/StyledElement';
import { checkoutTypes } from '../enums/checkout-types';
import DocumentBackground from '../components/DocumentBackground';

export default function CheckoutDouble() {
  const {
    theme,
    checkout,
    seller,
    query,
    isUpsell,
    product,
    isScreenshot,
    isConfirmation,
    checkoutType,
    checkoutLayout,
    previewDeviceSize,
  } = useCheckoutState();
  const aiTarget = useAiTarget();
  const { productPriceAmount, setupFeeAmount } = useCartCost();
  const { effectiveDiscountedFromPrice, effectiveTrialPeriodDays, effectiveStartDate } =
    useEffectivePricing();

  const hasTicketTypes = checkout.type === PAGE_TYPES.EVENT;
  const hasVariants = product?.variants?.length > 0;

  // Check position settings for variants and tickets
  const variantsPosition = theme?.elements?.checkout?.variants?.position || 'form';
  const ticketGroupsPosition = theme?.elements?.checkout?.ticketGroups?.position || 'form';
  // Prices default to 'content' (inside the Product block); 'form' moves the
  // picker into this column, mirroring the variants position option.
  const pricesPosition = theme?.elements?.product?.pricePicker?.position || 'content';
  const shouldRenderPricesInForm =
    pricesPosition === 'form' &&
    theme?.elements?.product?.pricePicker?.enabled !== false &&
    isMultiPrice(product?.prices, query);

  const shouldRenderVariantsInContent = hasVariants && variantsPosition === 'content';
  const shouldRenderVariantsInForm = hasVariants && variantsPosition === 'form';

  // Bumps sit under the variants and follow them between columns by default,
  // but unlike variants they are not gated on the product having any: a page
  // with bumps and no variants still has to show them.
  const orderBumpsPosition =
    theme?.elements?.checkout?.form?.orderBumps?.position || variantsPosition;
  const shouldRenderBumpsInContent = orderBumpsPosition === 'content';
  const shouldRenderBumpsInForm = orderBumpsPosition === 'form';
  const shouldRenderTicketsInContent = hasTicketTypes && ticketGroupsPosition === 'content';
  const shouldRenderTicketsInForm = hasTicketTypes && ticketGroupsPosition === 'form';

  const dividerEnabled = theme.elements?.checkout?.divider?.enabled !== false;

  const hasProductModules = checkHasProductModules({
    theme,
    checkout,
    seller,
    query,
    isScreenshot,
    productPriceAmount,
    setupFeeAmount,
    discountedFromPrice: effectiveDiscountedFromPrice,
    trialPeriodDays: effectiveTrialPeriodDays,
    startDate: effectiveStartDate,
    checkoutType,
    checkoutLayout,
  });

  const hasEventModules = checkHasEventModules({
    theme,
    checkout,
    seller,
  });

  // Read layout settings from theme with fallback to checkout object
  const formPosition = theme?.layout?.doubleLayout?.formPosition || checkout.formPosition;
  const backgroundWidth = theme?.layout?.doubleLayout?.backgroundWidth || checkout.backgroundWidth;

  const showLogoBanner =
    checkoutType === checkoutTypes.CHECKOUT &&
    theme.elements?.product?.logo?.enabled !== false &&
    theme.elements?.product?.logo?.hostedPageLogoStyle !== 'logoBubble';

  // Stacked on phones: the confirmation hides the content column, leaving the form alone.
  const contentFirst = formPosition !== 'left' && !isConfirmation;
  const contentLast = formPosition === 'left' && !isConfirmation;

  return (
    <div className="cp_checkout-double-container relative">
      <DocumentBackground
        color={
          contentFirst ? 'var(--cp_content-background-color)' : 'var(--cp_form-background-color)'
        }
      />
      <LogoBanner className="@xl/checkout:max-w-[calc(50%-2rem)]" />

      <div
        className="cp_checkout-double-shell"
        data-background-width={backgroundWidth}
        data-form-position={formPosition}
      >
        <div className="mx-auto max-w-screen-xl">
          <div
            {...(isUpsell && aiTarget('upsell'))}
            className={clsx(
              'cp_checkout cp_checkout-double min-h-screen flex flex-col @xl/checkout:grid @xl/checkout:grid-cols-2 overflow-auto text-[var(--cp_text-primary)]',
              contentLast
                ? '@max-xl/checkout:bg-[var(--cp_content-background-color)]'
                : '@max-xl/checkout:bg-[var(--cp_form-background-color)]',
            )}
          >
            <div
              className={clsx(
                formPosition === 'left' ? 'order-2' : 'order-1',
                'cp_checkout-double-content bg-[var(--cp_content-background-color)]',
              )}
            >
              <div
                className={clsx(
                  'cp_card mx-auto max-w-screen-xs px-4 pb-4 @xl/checkout:pt-20 @xl/checkout:pb-20',
                  {
                    'pt-19': showLogoBanner,
                    'pt-8': !showLogoBanner,
                    '@xl/checkout:invisible @xl/checkout:block!': isConfirmation,
                    hidden: isConfirmation,
                  },
                )}
              >
                {hasEventModules && <EventDetails />}

                {hasProductModules && <Product />}

                {shouldRenderTicketsInContent && (
                  <div className="cp_ticket-types-wrapper -mx-4 mt-4">
                    <TicketGroups />
                  </div>
                )}

                {shouldRenderVariantsInContent && (
                  <div className="cp_variants-wrapper -mx-4 mt-4">
                    <Variants />
                  </div>
                )}

                {shouldRenderBumpsInContent && (
                  <div className="cp_order-bumps-wrapper mt-4">
                    <OrderBumps />
                  </div>
                )}
              </div>

              <StyledElement
                elementPath="checkout.divider"
                className={clsx(
                  'cp_divider border-b border-[var(--cp_checkout-divider-border-color)]',
                  {
                    hidden: isConfirmation,
                    '@xl/checkout:hidden': !isConfirmation,
                  },
                )}
              />
            </div>

            <div
              className={clsx(
                'cp_checkout-double-form',
                {
                  'bg-[var(--cp_form-background-color)]':
                    backgroundWidth === 'half' && previewDeviceSize !== 'mobile', // on preview mobile, we add form background color in Preview.j
                  '@xl/checkout:shadow-[var(--cp_checkout-form-background-shadow-left)]':
                    formPosition !== 'left' && backgroundWidth !== 'full',
                  '@xl/checkout:shadow-[var(--cp_checkout-form-background-shadow-right)]':
                    formPosition === 'left' && backgroundWidth !== 'full',
                  grow: backgroundWidth === 'full',
                },
                formPosition === 'left' ? 'order-1' : 'order-2',
              )}
              data-background-width={backgroundWidth}
              style={{ '--cp_branding-badge-color': 'var(--cp_branding-badge-color-on-form)' }}
            >
              {isConfirmation && (
                <div
                  className={clsx('cp_checkout-double-form-shell cp_card mx-auto', {
                    'max-w-screen-xs': backgroundWidth === 'half',
                    '@xl/checkout:max-w-screen-xs': backgroundWidth === 'full',
                    '@xl/checkout:my-20': backgroundWidth === 'full',
                  })}
                  data-background-width={backgroundWidth}
                >
                  <div
                    className={clsx('cp_card', {
                      '@xl/checkout:bg-[var(--cp_form-background-color)]':
                        backgroundWidth === 'full',
                      'pt-20 @xl/checkout:pt-4 @xl/checkout:px-4 sm:mx-4 @xl/checkout:rounded-[var(--cp_checkout-border-radius)] @xl/max-w-screen-xs mx-auto':
                        backgroundWidth === 'full',
                      'pt-20 @xl/checkout:px-4 pb-12 @xl/checkout:pt-20':
                        backgroundWidth === 'half',
                    })}
                  >
                    <CheckoutConfirmation />
                  </div>
                </div>
              )}

              {!isConfirmation && (
                <div
                  className={clsx('cp_checkout-double-form-shell cp_card mx-auto', {
                    'max-w-screen-xs': backgroundWidth === 'half',
                    '@xl/checkout:max-w-screen-xs': backgroundWidth === 'full',
                    '@xl/checkout:my-20': backgroundWidth === 'full',
                  })}
                  data-background-width={backgroundWidth}
                >
                  <div
                    className={clsx('cp_card', {
                      '@xl/checkout:bg-[var(--cp_form-background-color)]':
                        backgroundWidth === 'full',
                      '@xl/checkout:pt-4 px-4 pb-4 @xl/checkout:mx-4 @xl/checkout:rounded-[var(--cp_checkout-border-radius)] @xl/max-w-screen-xs mx-auto':
                        backgroundWidth === 'full',
                      'px-4 pb-12 @xl/checkout:pt-20': backgroundWidth === 'half',
                      'pt-19': contentLast && showLogoBanner,
                      'pt-4': !(contentLast && showLogoBanner),
                    })}
                  >
                    <CheckoutFormContainer>
                      {shouldRenderPricesInForm && <ProductPricePicker className="pb-4" />}

                      {shouldRenderTicketsInForm && (
                        <div className="cp_ticket-types-wrapper -mx-4 pb-4">
                          <TicketGroups />
                        </div>
                      )}

                      {shouldRenderVariantsInForm && (
                        <div className="cp_variants-wrapper -mx-4 pb-4">
                          <Variants />
                        </div>
                      )}

                      {shouldRenderBumpsInForm && (
                        <div className="cp_order-bumps-wrapper pb-4">
                          <OrderBumps />
                        </div>
                      )}

                      {(shouldRenderPricesInForm ||
                        shouldRenderTicketsInForm ||
                        shouldRenderVariantsInForm) && (
                        <StyledElement
                          elementPath="checkout.divider"
                          className={clsx(
                            'cp_divider border-b border-[var(--cp_checkout-divider-border-color)]',
                          )}
                        />
                      )}

                      <div
                        className={clsx({
                          // The in-form blocks carry pb-4, so the fields only need
                          // mt-4 to clear the divider when it actually renders.
                          'mt-4':
                            dividerEnabled &&
                            (shouldRenderPricesInForm ||
                              shouldRenderTicketsInForm ||
                              shouldRenderVariantsInForm),
                        })}
                      >
                        <CheckoutForm />
                      </div>
                    </CheckoutFormContainer>

                    <CheckoutBrandingBadge />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
